import React, { useState, useEffect, useCallback } from 'react';
import {
  formatUSPhoneNumber,
  isValidUSPhoneNumber,
  scrubPhoneNumber,
} from '@express-labs/raven-tools';
import Button from '../Button/Button';
import s from './TextDirections.scss';

export interface TextDirectionsArgTypeProps {
  storeNumber: string;
  phoneNumber: string | null;
}

export interface TextDirectionsProps {
  onTextAddress: (args: TextDirectionsArgTypeProps) => void;
  storeNumber: string;
}

/**
Used in one spot in the app to text directions to a store to a valid phone number. The phone number must be in this form to be considered valid (614) 555-5555
## Examples
```javascript
 <TextDirections
    storeNumber={'0955'}
    onTextAddress={() => {}}
  />
```
**/

const TextDirections: React.FC<TextDirectionsProps> = ({
  onTextAddress,
  storeNumber,
}) => {
  const [hasError, setHasError] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [sentMessage, setSentMessage] = useState(false);

  useEffect(() => {
    if (sentMessage) {
      const timer = setTimeout(() => {
        setSentMessage(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [sentMessage]);

  const onFormSubmit = useCallback(
    (
      e:
        | React.MouseEvent<HTMLElement, MouseEvent>
        | React.FormEvent<HTMLFormElement>
    ) => {
      e.preventDefault();

      if (!hasError && typeof onTextAddress === 'function') {
        onTextAddress({
          storeNumber,
          phoneNumber: scrubPhoneNumber(inputValue),
        });
        setHasError(false);
        setSentMessage(true);
        return true;
      }
      //onFormSubmit function will only return true if there are no errors (hasError is false) and the onTextAddress prop is a valid function. Therefore, in the context of this component, the onFormSubmit function will always return true as long as the component is used correctly with valid prop values.
      //istanbul ignore next
      return false;
    },
    [storeNumber, inputValue]
  );

  const captureInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  }, []);

  const blurredPhoneNumber = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const number = e.target.value;

      if (number && number !== '') {
        if (isValidUSPhoneNumber(number)) {
          setHasError(false);
          setInputValue(formatUSPhoneNumber(number));
        } else {
          setHasError(true);
          setInputValue(number);
        }
      } else {
        setHasError(false);
      }
    },
    []
  );

  return (
    <section className={s.textAddressForm}>
      <form onSubmit={onFormSubmit}>
        <div>
          <label
            className={`labelActive ${s.phoneLabel}`}
            htmlFor="phoneNumber"
          >
            Phone Number
          </label>
          {hasError && (
            <span className={`labelError ${s.phoneNumberError}`} role="alert">
              Please enter a valid phone number.
            </span>
          )}
          {sentMessage && (
            <span className={`${s.phoneNumberError}`} role="alert">
              Message sent.
            </span>
          )}
          <input
            className={s.phoneInput}
            name="phoneNumber"
            id="phoneNumber"
            placeholder="Enter Phone Number"
            onChange={captureInput}
            onBlur={blurredPhoneNumber}
            value={inputValue}
            aria-required="true"
            aria-invalid={hasError ? 'true' : 'false'}
            type="tel"
            required
          />
          <Button
            as="button"
            mode="solid"
            theme="dark"
            size="small"
            onClick={onFormSubmit}
            disabled={inputValue === ''}
          >
            Send
          </Button>
        </div>
      </form>
    </section>
  );
};

TextDirections.displayName = 'TextDirections';

export default TextDirections;
